/**
 * The `mention` Quill format: an atomic inline chip rendered as
 *
 *   <span class="mention" data-user-id="123">@Jane Doe</span>
 *
 * Because the chip is an embed rather than styled text, deleting it removes the whole
 * mention — the user id can never be left behind by a partial edit. That markup is the
 * contract the server reads mentions back out of; see `parseMentionedUserIds` in
 * RCG_GraphQL/src/typeGraphql/Notes/noteMentions.ts and change the two together.
 */

export const MENTION_BLOT_NAME = "mention";
export const MENTION_CLASS_NAME = "mention";
export const MENTION_USER_ID_ATTRIBUTE = "data-user-id";

/**
 * Zero-width guards Quill's embed base puts either side of the chip's content to give
 * the caret somewhere to sit. Invisible, but part of `textContent`.
 */
export const EMBED_GUARD_CHARACTERS = /\uFEFF/g;

export interface MentionValue {
  userId: number;
  name: string;
}

/**
 * The slice of the Quill constructor this registration needs. Declared structurally so
 * the same helper can register the format on Quill 2 (`react-quill-new`, used by the
 * editors) and Quill 1 (`react-quill`, used by the read-only note views).
 */
export interface QuillRegistry {
  import(path: string): unknown;
  register(path: string, target: unknown, overwrite?: boolean): void;
}

/**
 * The parts of Quill's `blots/embed` base class the mention blot builds on. Quill types
 * `import()` as `unknown`, so this is the narrowing at that boundary.
 */
interface EmbedBlotClass {
  new (...args: unknown[]): { domNode: HTMLElement };
  create(value: unknown): HTMLElement;
}

const registered = new WeakSet<QuillRegistry>();

/**
 * Idempotently registers the mention format. Safe to call on every render and on both
 * Quill packages; re-registering the same constructor is a no-op.
 */
export function registerMentionBlot(Quill: QuillRegistry) {
  if (registered.has(Quill)) return;
  registered.add(Quill);

  const Embed = Quill.import("blots/embed") as EmbedBlotClass;

  class MentionBlot extends Embed {
    static blotName = MENTION_BLOT_NAME;
    static tagName = "SPAN";
    static className = MENTION_CLASS_NAME;

    static create(value: MentionValue) {
      const node = super.create(value);
      node.setAttribute(MENTION_USER_ID_ATTRIBUTE, String(value.userId));
      node.textContent = `@${value.name}`;
      return node;
    }

    static value(domNode: HTMLElement): MentionValue {
      return {
        userId: Number(domNode.getAttribute(MENTION_USER_ID_ATTRIBUTE)),
        // Quill's embed base wraps the content in zero-width guard characters, so they
        // have to come off before the name round-trips back into a value.
        name: (domNode.textContent ?? "")
          .replace(EMBED_GUARD_CHARACTERS, "")
          .replace(/^@/, ""),
      };
    }
  }

  Quill.register(`formats/${MENTION_BLOT_NAME}`, MentionBlot, true);
}
